
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Bell } from "lucide-react";
import { useState } from "react";
import { Pagination, PaginationContent, PaginationItem, PaginationLink, PaginationNext, PaginationPrevious } from "@/components/ui/pagination";

interface Notification {
  id: string;
  title: string;
  seen: boolean;
  created_at: string;
}

interface NotificationsListProps {
  notifications: Notification[];
  unreadCount: number;
}

export const NotificationsList = ({ notifications, unreadCount }: NotificationsListProps) => {
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 3;
  
  // Calculate pagination values
  const totalPages = Math.ceil(notifications.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = Math.min(startIndex + itemsPerPage, notifications.length);
  const paginatedNotifications = notifications.slice(startIndex, endIndex);
  
  return (
    <Card className="md:col-span-2">
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center text-lg">
          <Bell className="mr-2 h-5 w-5 text-nation-blue" />
          My Notifications
        </CardTitle>
        {unreadCount > 0 && (
          <CardDescription>
            You have {unreadCount} unread {unreadCount === 1 ? 'notification' : 'notifications'}
          </CardDescription>
        )}
      </CardHeader>
      <CardContent>
        {notifications.length === 0 ? (
          <div className="text-center py-4 text-muted-foreground">
            No notifications
          </div>
        ) : (
          <>
            <div className="space-y-3 max-h-[200px] overflow-y-auto">
              {paginatedNotifications.map((notification) => (
                <div
                  key={notification.id}
                  className={`text-sm p-2 rounded-md ${
                    !notification.seen
                      ? 'bg-blue-50 dark:bg-blue-900/20'
                      : 'bg-gray-50 dark:bg-gray-800'
                  }`}
                >
                  <div className="font-medium">{notification.title}</div>
                  <div className="text-xs text-muted-foreground mt-1">
                    {new Date(notification.created_at).toLocaleDateString()}
                  </div>
                </div>
              ))}
            </div>
            
            {totalPages > 1 && (
              <div className="mt-4">
                <Pagination>
                  <PaginationContent>
                    <PaginationItem>
                      <PaginationPrevious 
                        onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                        className={currentPage === 1 ? "pointer-events-none opacity-50" : ""}
                      />
                    </PaginationItem>
                    
                    {Array.from({length: Math.min(totalPages, 3)}).map((_, i) => {
                      // For simplicity, just show max 3 page numbers
                      let pageNum = i + 1;
                      if (totalPages > 3 && currentPage > 2) {
                        pageNum = currentPage - 1 + i;
                        if (pageNum > totalPages) {
                          pageNum = totalPages - (2 - i);
                        }
                      }
                      
                      return (
                        <PaginationItem key={pageNum}>
                          <PaginationLink 
                            isActive={currentPage === pageNum}
                            onClick={() => setCurrentPage(pageNum)}
                          >
                            {pageNum}
                          </PaginationLink>
                        </PaginationItem>
                      );
                    })}
                    
                    <PaginationItem>
                      <PaginationNext 
                        onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                        className={currentPage === totalPages ? "pointer-events-none opacity-50" : ""}
                      />
                    </PaginationItem>
                  </PaginationContent>
                </Pagination>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};
